import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

// Browser API shim (IndexedDB, cloud LLM/export) — same as desktop-v2 web SPA
import "../../desktop-v2/src/renderer-react/services/installBrowserDesktopV2Api";
import { installStraightQuotes } from "@repo/shared/installStraightQuotes";

import { App } from "../../desktop-v2/src/renderer-react/App";

import "@fontsource/eb-garamond/400.css";
import "@fontsource/eb-garamond/400-italic.css";
import "@fontsource/eb-garamond/700.css";
import "@fontsource/eb-garamond/700-italic.css";
import "@fontsource/crimson-text/400.css";
import "@fontsource/crimson-text/400-italic.css";
import "@fontsource/crimson-text/700.css";
import "@fontsource/crimson-text/700-italic.css";
import "@fontsource/libre-baskerville/400.css";
import "@fontsource/libre-baskerville/400-italic.css";
import "@fontsource/libre-baskerville/700.css";
import "@fontsource/libre-baskerville/700-italic.css";
import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/400-italic.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/playfair-display/700-italic.css";
import "@fontsource/cinzel/400.css";
import "@fontsource/cinzel/700.css";

import "../../desktop-v2/src/renderer-react/styles.css";
import "@justwrite/ui/styles/tokens.css";
import "./premier.css";

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[premier] React render error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-dvh bg-background p-6 text-foreground">
          <h1 className="text-lg font-semibold">Just Write could not start</h1>
          <pre className="mt-3 whitespace-pre-wrap text-xs text-destructive">
            {this.state.error.stack ?? this.state.error.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

document.documentElement.dataset.premier = "true";
installStraightQuotes();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing root element");
}

createRoot(rootElement).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);
